import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { Badge, CustomTable, Text } from '../../../shared/uiComponents';
import { Puzzle, UserTie } from '../../../shared/Assets/menuIcons';
import { useScreenWidth } from '../../../shared/Helpers/hooks';
import { TEXT } from '../../../shared/Helpers/colors';
import { BadgeTypes, SoapNoteStatusTypes } from '../../../shared/uiComponents/StatusBadge';
import { CustomPagination } from '../../../shared/uiComponents/Table/pagination';
import { TableBodySessions } from './tableInfo';
import { FLAGGED_SESSIONS_HEADERS } from '../helpers';
import { FLAGGED_SESSIONS_DESCRIPTION, FLAGGED_SESSIONS_TITLE } from '../../../shared/Helpers/constants';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { FlagggedSessionsTableData, getFlaggedSessions } from '../../../redux/State/clientSlice/dashboardSlice';
import EditSessionDate from './EditSessionDate';

const MobileView = ({ data }: { data: FlagggedSessionsTableData }) => {
	if (!data.query?.length) {
		return (
			<div className="flaggedSessionMobileCardNoResultsContainer">
				<Text title="No flagged sessions" type="plg" fontWeight="semiBold" />
			</div>
		);
	}

	return (
		<div>
			<div className="flaggedSessionMobileCardContainer">
				{data.query?.map((row) => (
					<div className="flaggedSessionMobileCard" key={`flagged-${row.id}`}>
						<div className="flaggedSessionMobileCardHeader">
							<div className="flaggedSessionMobileCardHeaderIcon">
								<UserTie />
							</div>
							<Text title={row.user.fullName} type="pmd" fontWeight="medium" />
							<EditSessionDate row={row} />
						</div>
						<div className="flaggedSessionMobileCardDivider" />
						<div className="flaggedSessionMobileCardBody">
							<div className="flaggedSessionMobileCardBodyItem">
								<Text title="Client Name:" type="pxs" color={TEXT.TERTIARY} />
								<Text title={row.client.fullName} type="psm" fontWeight="medium" />
							</div>
							<div className="flaggedSessionMobileCardBodyItem">
								<Text title="Session Type:" type="pxs" color={TEXT.TERTIARY} />
								<Text title={row.sessionType.name} type="psm" fontWeight="medium" />
							</div>
							<div className="flaggedSessionMobileCardBodyItem">
								<Text title="Session Date:" type="pxs" color={TEXT.SECONDARY} />
								<Text title={dayjs(row.date).format('MM/DD/YYYY')} type="psm" fontWeight="medium" />
							</div>
							{Boolean(row.reportStatus) && (
								<div className="flaggedSessionMobileCardBodyItem">
									<Text title="Report Status:" type="pxs" color={TEXT.SECONDARY} />
									<Badge
										label={row?.reportStatus?.name ?? ''}
										sizeType="sm"
										type={SoapNoteStatusTypes[row?.reportStatus?.status ?? 'gray'] as BadgeTypes}
									/>
								</div>
							)}
						</div>
					</div>
				))}
			</div>
			<CustomPagination data={data} type="sessions" />
		</div>
	);
};

const FlaggedSessions = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const page = params.get('sessionsPage') || '1';
	const pageSize = params.get('sessionsPageSize') || '8';

	const loading = useSelector((state) => state.dashboard.loading);
	const sessions = useSelector((state) => state.dashboard.sessions);

	useEffect(() => {
		dispatch(getFlaggedSessions({ page, pageSize }));
	}, [dispatch, page, pageSize]);

	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	return (
		<div className="dashboardCard" style={{ width: '100%', marginTop: '32px' }}>
			<div className="dashboardHeader">
				<div className="header">
					<div className="headerIcon">
						<Puzzle />
					</div>
					<div className="headerTitle">
						<Text type="h6" font="header" fontWeight="semiBold" title={FLAGGED_SESSIONS_TITLE} />
						{Boolean(sessions.query?.length) && (
							<Text type="psm" fontWeight="medium" color={TEXT.TERTIARY} title={FLAGGED_SESSIONS_DESCRIPTION} />
						)}
					</div>
				</div>
			</div>
			{isMobile ? (
				<MobileView data={sessions} />
			) : (
				<CustomTable
					headers={FLAGGED_SESSIONS_HEADERS}
					data={sessions}
					TableBody={TableBodySessions}
					loading={loading}
					type="sessions"
				/>
			)}
		</div>
	);
};

export default FlaggedSessions;
